import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/react';
import AuthWrapper, { useAuth } from './components/auth/AuthWrapper';
import AppShell from './components/layout/AppShell';
import ErrorBoundary from './components/ErrorBoundary';
import { PortfolioDataProvider } from './contexts/PortfolioDataContext';
import { MemoryProvider } from './contexts/MemoryContext';
import { AgentProvider, useAgent } from './contexts/AgentContext';
import './App.css';
import { setAuthTokenProvider } from './utils/authFetch';
import { captureReferralCode } from './utils/referralCapture';
import { QuotaModalProvider } from './components/quota/QuotaModalProvider';
import { useSupabaseAuth } from './hooks/useSupabaseAuth';
import { lazyWithRetry } from './utils/lazyRetry';

// lazyWithRetry, not React.lazy: a transiently failed chunk fetch would
// otherwise be memoized as rejected for the whole page lifetime (prod
// 2026-08-26 — SettingsPage dead all session while the chunk was 200-OK).
const AskBar = lazyWithRetry(() => import('./components/agent/AskBar'), 'AskBar');
const AskBarTrigger = lazyWithRetry(() => import('./components/agent/AskBarTrigger'), 'AskBarTrigger');
const AdvocateRail = lazyWithRetry(() => import('./components/agent/AdvocateRail'), 'AdvocateRail');
const WorkCanvas = lazyWithRetry(() => import('./components/agent/WorkCanvas'), 'WorkCanvas');
const FeedbackTab = lazyWithRetry(() => import('./components/feedback/FeedbackTab'), 'FeedbackTab');

const HomePage = lazyWithRetry(() => import('./pages/HomePage'), 'HomePage');
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'), 'LandingPage');
const LandingFilmPage = lazyWithRetry(() => import('./pages/LandingFilmPage'), 'LandingFilmPage');
const FeaturesPage = lazyWithRetry(() => import('./pages/FeaturesPage'), 'FeaturesPage');
const HowItWorksPage = lazyWithRetry(() => import('./pages/HowItWorksPage'), 'HowItWorksPage');
const DueDiligencePage = lazyWithRetry(() => import('./pages/DueDiligencePage'), 'DueDiligencePage');
const DealCopilotPage = lazyWithRetry(() => import('./pages/DealCopilotPage'), 'DealCopilotPage');
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'), 'PricingPage');
const PartnerJoinPage = lazyWithRetry(() => import('./pages/PartnerJoinPage'), 'PartnerJoinPage');
const SuburbSearchPage = lazyWithRetry(() => import('./pages/SuburbSearchPage'), 'SuburbSearchPage');
const PortfolioDashboard = lazyWithRetry(() => import('./pages/PortfolioDashboard'), 'PortfolioDashboard');
const PropertyDetailPage = lazyWithRetry(() => import('./pages/PropertyDetailPage'), 'PropertyDetailPage');
const PropertySearch = lazyWithRetry(() => import('./pages/PropertySearch'), 'PropertySearch');
const DealsListPage = lazyWithRetry(() => import('./pages/DealsListPage'), 'DealsListPage');
const DealDetailPage = lazyWithRetry(() => import('./pages/DealDetailPage'), 'DealDetailPage');
const StrategyPage = lazyWithRetry(() => import('./pages/StrategyPage'), 'StrategyPage');
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'), 'SettingsPage');
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'), 'TermsPage');
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'), 'PrivacyPage');
const EngineDebugPage = lazyWithRetry(() => import('./components/Debug/EngineDebugPage'), 'EngineDebugPage');
const PortfolioOnboarding = lazyWithRetry(() => import('./components/Portfolio/Onboarding'), 'PortfolioOnboarding', (m) => ({ default: m.PortfolioOnboarding }));

function ProtectedRoute({ children }) {
  const { isDevelopment } = useAuth();

  // In dev mode bypass all checks
  if (isDevelopment) return children;

  // On prod — read directly from Clerk
  return <ClerkProtectedRoute>{children}</ClerkProtectedRoute>;
}

export function ClerkProtectedRoute({ children }) {
  const { isLoaded, isSignedIn } = useUser();

  if (!isLoaded) return null;

  // Any signed-in user gets in: sign-up is self-serve (public mode) and
  // lockout is Clerk's job (ban/revoke) plus the ComplianceGate, not an
  // in-app role list. The old allowlist (absent|admin|user) silently
  // <Navigate>'d every OTHER publicMetadata.role value home — tagging an
  // approved user e.g. role="beta" in Clerk bricked all protected routes
  // with no visible error.
  return isSignedIn ? children : <Navigate to="/" replace />;
}

/** ADR-017 §11.4 — global Cmd+/ (with Cmd+K fallback) toggles the AskBar. */
function useAskBarShortcut() {
  const { setIsAskBarOpen } = useAgent();
  React.useEffect(() => {
    const handler = (e) => {
      // Cmd+/ (mac) or Ctrl+/ — primary
      // Cmd+K / Ctrl+K — fallback for users with browser keymap conflicts on /
      const isPrimary = (e.metaKey || e.ctrlKey) && e.key === '/';
      const isFallback = (e.metaKey || e.ctrlKey) && e.key === 'k';
      if (isPrimary || isFallback) {
        e.preventDefault();
        setIsAskBarOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setIsAskBarOpen]);
}

function AppLayout() {
  const { getAuthToken, user, isDevelopment } = useAuth();
  // Set synchronously — must happen before children render and fire API calls
  setAuthTokenProvider(getAuthToken);

  // ADR-016 Sprint C1 — keeps the Supabase client JWT in sync with Clerk
  // so Progress Ledger realtime subscriptions can apply RLS policies.
  useSupabaseAuth();

  // ADR-017 PR 3 — global Cmd+/ keybind
  useAskBarShortcut();

  const { pathname } = useLocation();

  // WCAG 2.4.2 + 2.4.3 — update document title and move focus to main content
  // on every route change so screen readers announce the new page.
  useEffect(() => {
    const PAGE_TITLES = {
      '/': 'Home',
      '/suburb-search': 'Suburb Search',
      '/prop-search': 'Property Research',
      '/portfolio': 'Portfolio',
      '/portfolio/onboarding': 'Portfolio Setup',
      '/deals': 'Deals',
      '/strategy': 'Strategy',
      '/settings': 'Settings',
      '/debug/engine': 'Engine Debug',
    };
    const match = Object.keys(PAGE_TITLES).find(k => pathname === k || (k !== '/' && pathname.startsWith(k)));
    const label = match ? PAGE_TITLES[match] : null;
    document.title = label ? `${label} — Heyward` : 'Heyward — Your End-to-End Real Estate AI Agent';

    const main = document.getElementById('main-content');
    if (main) main.focus();
  }, [pathname]);

  // Unauthed visitors landing on `/` get the public marketing page, rendered
  // outside the AppShell (no sidebar, no agent infra, no auth-required data).
  // Dev mode treats the user as signed-in so we never show landing on `/`
  // locally — preview the marketing page at `/landing` instead.
  const isLandingPreview = pathname === '/landing';
  const isUnauthedRoot = pathname === '/' && !isDevelopment && !user?.userId;
  if (isLandingPreview || isUnauthedRoot) {
    return <LandingPage />;
  }

  const transparentRoutes = ['/property', '/strategy', '/portfolio/onboarding'];
  const variant = transparentRoutes.some(r => pathname.startsWith(r))
    ? 'transparent' : 'default';

  return (
    <AppShell variant={variant}>
      <QuotaModalProvider>
      <Suspense fallback={null}>
        <AskBar />
        <AskBarTrigger />
        <AdvocateRail />
        <WorkCanvas />
        <FeedbackTab />
      </Suspense>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        {/* Signed-in users who click a landing CTA (e.g. "Start free")
            end up here. Bounce them home rather than rendering AppShell
            with no route content. Signed-out users never reach this
            branch — they get the Clerk SignInPage via SignedOutGate. */}
        <Route path="/sign-in" element={<Navigate to="/" replace />} />
        <Route path="/suburb-search" element={<SuburbSearchPage />} />
        <Route path="/prop-search" element={<PropertySearch />} />
        <Route path="/portfolio" element={<ProtectedRoute><PortfolioDashboard /></ProtectedRoute>} />
        <Route path="/portfolio/onboarding" element={<ProtectedRoute><PortfolioOnboarding /></ProtectedRoute>} />
        <Route path="/property/:propertyId" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
        <Route path="/deals" element={<ProtectedRoute><DealsListPage /></ProtectedRoute>} />
        <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetailPage /></ProtectedRoute>} />
        <Route path="/strategy" element={<ProtectedRoute><StrategyPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        {/* ADR-020: engine debug surface — gated by useEngineDebugVisible (dev / admin only). */}
        <Route path="/debug/engine" element={<ProtectedRoute><EngineDebugPage /></ProtectedRoute>} />
        {/* Catch-all — redirect unknown URLs to home instead of blank AppShell */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </QuotaModalProvider>
    </AppShell>
  );
}

/** Capture first-touch referral codes before route-specific effects run. */
function ReferralCaptureEffect() {
  const { search } = useLocation();

  useEffect(() => {
    captureReferralCode();
  }, [search]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ReferralCaptureEffect />
        <Suspense fallback={null}>
        <Routes>
          {/* Public legal pages — accessible without authentication */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* Public marketing landing — no auth, no AppShell, no dev banner.
              Authed visitors hitting `/` still get landing routed via
              AppLayout below; this route just serves the dev/admin preview. */}
          <Route path="/landing" element={<LandingPage />} />
          {/* "Watch Heyward Work" scroll-film landing (film bible:
              docs/design/landing-film-concept-a.md). Preview route while in
              taste rounds; replaces `/` for unauthed at ship gate. */}
          <Route path="/landing-v2" element={<LandingFilmPage />} />
          {/* Public marketing /features — hosts the Due Diligence + Deal
              Copilot sections previously inlined on the landing page.
              Same chrome (LandingNav + LandingFinal), no auth, no AppShell. */}
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          {/* Dedicated, indexable per-feature pages (own <title>, own URL)
              so each feature can rank on its own. Reuse the same section
              components as the combined /features page. */}
          <Route path="/features/due-diligence" element={<DueDiligencePage />} />
          <Route path="/features/deal-copilot" element={<DealCopilotPage />} />
          {/* Public pricing (ADR-025) — same chrome, no auth, no AppShell. */}
          <Route path="/pricing" element={<PricingPage />} />
          {/* Partner purchase workspace (ADR-026 D6.1) — the page behind an
              invitation's join link. Public by design: the tokenized link IS
              the credential, verified server-side; the page itself renders
              noindex and the backend 404s while PARTNER_NETWORK_ENABLED is
              off, so this route exposes nothing while dark. */}
          <Route path="/partner/join/:token" element={<PartnerJoinPage />} />
          {/* All other routes — inside auth wrapper */}
          <Route path="/*" element={
            <AuthWrapper>
              <PortfolioDataProvider>
                <MemoryProvider>
                  <AgentProvider>
                    <AppLayout />
                  </AgentProvider>
                </MemoryProvider>
              </PortfolioDataProvider>
            </AuthWrapper>
          } />
        </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
